// Entry point for esbuild

/* eslint no-console:0 */

import * as Sentry from "@sentry/browser";

// Turbo's own Visit/FetchRequest classes call fetch without awaiting or
// catching failures (hotwired/turbo#170, open since 2021), so two families of
// uncatchable unhandled rejection reach Sentry. We can't fix Turbo's
// internals, so filter this known, non-actionable noise out instead of
// chasing every call site.
function isUnhandledTurboFetchRejection(error) {
  if (!error || typeof error.stack !== "string") return false;

  // A Turbo-driven navigation that failed on the network.
  if (error.stack.includes("fetchWithTurboHeaders")) {
    return /Load failed|Failed to fetch|NetworkError|AbortError/i.test(error.message || "");
  }

  // A request Turbo aborted itself because a second visit superseded it on the
  // same frame — two taps on one card is enough. The name carries "AbortError"
  // while the message is the browser's own wording ("Fetch is aborted"), and
  // Safari prints bare frame names, so neither the branch above nor a
  // class-qualified FetchRequest.cancel would match.
  return error.name === "AbortError" && /#loadSourceURL|#visit/.test(error.stack);
}

// Browser extensions inject their content scripts with no source URL, so every
// frame they throw from is "<anonymous>" and Sentry marks it in-app for want of
// anywhere better to attribute it (RUBY-RAILS-13M: a password manager's
// Form.submitHandler blowing up while it scraped our sign-in form). Our own
// code is always served from a URL, so a stack that names no script at all
// cannot be ours, and we can neither reproduce nor fix it.
const UNATTRIBUTED_FRAME = /^(<anonymous>|\[native code\])?$/;

function isInjectedScriptError(event) {
  const frames = (event.exception?.values || []).flatMap((value) => value.stacktrace?.frames || []);
  if (!frames.length) return false;
  return frames.every((frame) => UNATTRIBUTED_FRAME.test(frame.filename || ""));
}

const sentryDsn = document.querySelector('meta[name="sentry-dsn"]')?.content;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: document.querySelector('meta[name="sentry-environment"]')?.content || "production",
    tracesSampleRate: 0.005,
    beforeSend(event, hint) {
      if (isUnhandledTurboFetchRejection(hint?.originalException)) return null;
      if (isInjectedScriptError(event)) return null;
      return event;
    },
  });
}

// Sentry patches window.onunhandledrejection directly and ignores
// preventDefault, so this doesn't affect the beforeSend filter above — it
// only silences the browser's own "Uncaught (in promise)" console noise for
// the same known rejection classes.
window.addEventListener("unhandledrejection", (event) => {
  if (isUnhandledTurboFetchRejection(event.reason)) {
    event.preventDefault();
  }
});

// Must come before any vendor jQuery plugins so window.jQuery is set
// before their side-effecting imports run (esbuild hoists imports).
import "./src/jquery_global";
import $ from "jquery";

import "whatwg-fetch";

// Use relative paths to vendor files
import "../../vendor/assets/javascripts/jquery.ui.widget";
import "../../vendor/assets/javascripts/jquery.iframe-transport";
import "../../vendor/assets/javascripts/jquery.fileupload";
import "./src/jquery.sausage";
import "./src/file_uploader";

import Rails from "@rails/ujs";
import "@hotwired/turbo-rails";
import "@rails/activestorage";
import Flatpickr from "stimulus-flatpickr";

import { application } from "./controllers/application";
import "./controllers";

if (!window._rails_loaded) Rails.start();

application.register("flatpickr", Flatpickr); // Manually register Flatpickr as a stimulus controller

window.loadingContacts = false;
window.loadingLists = false;

// Fomantic-UI 2.9 JS components, matching the fomantic-ui-css stylesheet. These
// self-register $.fn.<plugin> against the global jQuery (set in jquery_global).
// transition first — the others use it for animations.
require("fomantic-ui-css/components/transition.js");
require("fomantic-ui-css/components/popup.js");
require("fomantic-ui-css/components/dropdown.js");
require("fomantic-ui-css/components/checkbox.js");

if (!Element.prototype.removeAll) {
  Element.prototype.removeAll = function () {
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }
    return this;
  };
}

// A stale session answers XHR with 401 and the sign in url (DeviseFailureApp).
// @rails/request.js follows it on its own; rails-ujs needs a hand.
document.addEventListener("ajax:error", (event) => {
  const xhr = event.detail[2];
  const signInUrl = xhr.getResponseHeader("WWW-Authenticate");
  if (xhr.status === 401 && signInUrl) window.location.assign(signInUrl);
});

Turbo.StreamActions.redirect = function () {
  Turbo.visit(this.target);
};

// Close an open modal (and dimmer), then remove the target element
Turbo.StreamActions.close_modal = function () {
  // Ask the modals Stimulus controller to close via its central logic
  document.dispatchEvent(new CustomEvent("modalClose", { bubbles: true }));
};

// Locate dashboard activity: the server names the intent (add a request, or
// refresh a request's message/location pane). Each action does its DOM update,
// then announces the activity so the locate controller can run the matching
// side effects (unread badges, focus, notification).
const locateActivity = (kind, domAction) =>
  function () {
    Turbo.StreamActions[domAction].call(this);
    document.dispatchEvent(
      new CustomEvent("locate:activity", {
        bubbles: true,
        detail: { kind, target: this.target },
      })
    );
  };
// New requests prepend so the newest is on top, matching the server-rendered
// list (created_at desc).
Turbo.StreamActions.locate_prepend = locateActivity("added", "prepend");
Turbo.StreamActions.locate_message = locateActivity("message", "replace");
Turbo.StreamActions.locate_location = locateActivity("location", "replace");

const pollActivity = (domAction) =>
  function () {
    Turbo.StreamActions[domAction].call(this);
    document.dispatchEvent(new CustomEvent("poll:activity", { bubbles: true }));
  };
Turbo.StreamActions.poll_prepend = pollActivity("prepend");
Turbo.StreamActions.poll_replace = pollActivity("replace");

Turbo.StreamActions.text_message_prepend = function () {
  const wrapper = document.createElement("div");
  wrapper.classList.add("cabled-in");
  wrapper.append(this.templateContent);
  const inbound = wrapper.querySelector(".text-message.inbound") !== null;
  this.targetElements.forEach((target) => target.prepend(wrapper));
  document.dispatchEvent(
    new CustomEvent("text-message:activity", { bubbles: true, detail: { inbound } })
  );
};

document.addEventListener("turbo:frame-missing", function (event) {
  event.preventDefault();
  window.location.reload();
});

window.initMap = async () => {
  const element = document.getElementById("map");
  if (!element) return;
  await Promise.all([
    google.maps.importLibrary("maps"),
    google.maps.importLibrary("marker"),
    google.maps.importLibrary("core"),
  ]);
  window.google = google;
  const map = new google.maps.Map(element, {
    center: { lat: 0, lng: 0 },
    zoom: 1,
    zoomControl: true,
    scaleControl: true,
    mapTypeControl: true,
    mapTypeId: "terrain",
    mapTypeControlOptions: {
      mapTypeIds: ["satellite", "terrain"],
    },
  });
  window.map = map;
  window.markers = [];
  window.circles = [];
  if (element.dataset.locationPayload) {
    var event = new CustomEvent("maps-update-location", {
      bubbles: true,
      cancelable: true,
    });
    element.dispatchEvent(event);
  }
};

$(document).on("click", "form .delete-record", function (event) {
  $(this).prev("input[type=hidden]").val("1");
  $(this).closest("fieldset, tr").hide();
  return event.preventDefault();
});
